import { Checkbox as CheckboxPrimitive } from '@base-ui/react/checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { CheckIcon, MinusIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';

const checkboxLayout = 'peer relative inline-flex align-middle shrink-0 overflow-hidden rounded';
const checkboxBorder = 'border-2 border-input hover:border-primary';
const checkboxFocus = 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';
const checkboxBase = 'bg-background transition-colors data-disabled:cursor-not-allowed data-disabled:opacity-50';
const checkboxChecked = cn(
  'data-checked:border-primary data-checked:bg-primary',
  'data-indeterminate:border-primary data-indeterminate:bg-primary'
);

const checkboxVariants = cva(cn(checkboxLayout, checkboxBorder, checkboxFocus, checkboxBase, checkboxChecked), {
  variants: {
    size: {
      sm: 'size-3.5',
      default: 'size-5',
    },
  },
  defaultVariants: { size: 'default' },
});

const indicatorIconVariants = cva('pointer-events-none fill-white [&_path]:fill-white', {
  variants: {
    size: {
      sm: 'size-2.5',
      default: 'size-3.5',
    },
  },
  defaultVariants: { size: 'default' },
});

interface CheckboxProps extends CheckboxPrimitive.Root.Props, VariantProps<typeof checkboxVariants> {
  className?: string;
  checked?: boolean;
}

export const Checkbox = React.forwardRef<React.ComponentRef<typeof CheckboxPrimitive.Root>, CheckboxProps>(
  ({ className, size, ...props }, ref) => (
    <CheckboxPrimitive.Root
      ref={ref}
      data-slot="checkbox"
      className={cn(checkboxVariants({ size }), className)}
      {...props}
    >
      <CheckboxPrimitive.Indicator
        keepMounted
        render={(indicatorProps, state) => (
          <span
            {...indicatorProps}
            className="absolute inset-0 flex items-center justify-center data-[unchecked]:invisible"
          >
            {state.indeterminate ? (
              <MinusIcon className={indicatorIconVariants({ size })} />
            ) : (
              <CheckIcon className={indicatorIconVariants({ size })} />
            )}
          </span>
        )}
      />
    </CheckboxPrimitive.Root>
  )
);

Checkbox.displayName = 'Checkbox';
